import { ButtonProps as ButtonPropsMui } from '@mui/material';
import { forwardRef, ReactNode, Ref } from 'react';
import { Typography } from '../Typography';
import { IconWrapper, StyledButton } from './Button.styles';

export type ColorOptions = 'primary' | 'destructive' | 'neutral' | 'info';
export type VariantOptions = 'contained' | 'outlined' | 'text';

const colorMapper: Record<ColorOptions, ButtonPropsMui['color']> = {
  primary: 'primary',
  destructive: 'error',
  neutral: 'text',
  info: 'info',
};

export interface ButtonProps
  extends Omit<ButtonPropsMui, 'color'>,
    Pick<
      ButtonPropsMui,
      'disabled' | 'fullWidth' | 'children' | 'loading' | 'loadingPosition'
    > {
  readonly label?: ReactNode;
  readonly color?: ColorOptions;
  readonly variant?: VariantOptions;
  readonly startIcon?: ReactNode;
  readonly ml?: string | number;
  readonly mr?: string | number;
  readonly mt?: string | number;
  readonly mb?: string | number;
  readonly pl?: string | number;
  readonly 'data-testid'?: string;
}

export const Button = forwardRef(
  (
    {
      label,
      color = 'primary',
      variant = 'contained',
      mr,
      ml,
      mt,
      mb,
      pl,
      startIcon,
      'data-testid': dataTestId,
      ...props
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const ariaLabel = typeof label === 'string' ? label : props['aria-label'];
    return (
      <StyledButton
        {...props}
        aria-label={ariaLabel}
        color={colorMapper[color]}
        data-testid={dataTestId}
        mb={mb}
        ml={ml}
        mr={mr}
        mt={mt}
        pl={pl}
        ref={ref}
        startIcon={
          label && startIcon ? <IconWrapper>{startIcon}</IconWrapper> : null
        }
        variant={variant}
      >
        {label ? <Typography variant="subtitle">{label}</Typography> : null}
        {!label && startIcon ? <IconWrapper>{startIcon}</IconWrapper> : null}
        {props.children}
      </StyledButton>
    );
  },
);
